// eslint-disable-next-line @altermaind/you-shall-not-pass
import { useMemo } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Asset, Box, Button, Svg, Typography, useThemeValue } from '@altermaind/web/ui';
import { ClaimsComponent } from '@/_shared/components/Claims';
import { Roles } from '@/_shared/constants/roles';
import { convertSizeToNumber } from '@/_shared/helpers/convertSizeToNumber';
import { getUserProfileCall } from '@/core/state/api';
import { useFavorite } from '@/training/contentsLikes/hooks/useFavorite';
import { CardsHeaderContentSkeleton } from './CardsHeaderContentSkeleton';

type Props = {
  contentId: string;
  title?: string;
  category?: string;
  rating?: number;
  isFavorite?: boolean;
  creditsCost?: number;
  imageAssetName?: string;
  thumbnailUrl?: string;
  mainInfoContent?: {
    text: string;
    iconName: (typeof Svg)['name'];
  };
  loading?: boolean;
  halfHeightLayout?: boolean;
  inProgress?: boolean;
  isCompleted?: boolean;
  isSkillToBoost?: boolean;
  hideFavoriteButton?: boolean;
};

export const CardsHeader = ({
  contentId,
  imageAssetName,
  thumbnailUrl,
  rating,
  isFavorite,
  creditsCost,
  title,
  category,
  mainInfoContent,
  loading,
  halfHeightLayout,
  inProgress = false,
  isCompleted = false,
  isSkillToBoost = false,
  hideFavoriteButton = false,
}: Props) => {
  const t = useTranslations('trainingSoftSkills.cards.GENERIC');
  const { userRole } = getUserProfileCall.hook();
  const isEmployeeOrManager = userRole === Roles.Employee || userRole === Roles.Manager;
  const primaryColor = useThemeValue('colors', 'primary');
  const primaryBackgroundColor = useThemeValue('colors', 'primary/background');
  const gapXS = convertSizeToNumber(`${useThemeValue('sizes', 'xs')}`);
  const gapS = convertSizeToNumber(`${useThemeValue('sizes', 's')}`);

  const cardHeaderHeight = halfHeightLayout ? 'auto' : 250;
  const assetSize = halfHeightLayout ? '100%' : 'auto';

  const {
    isFavorite: localIsFavorite,
    isLoading: isLocalLoading,
    toggleFavorite: onToggleFavorite,
  } = useFavorite({ contentId, initialIsFavorite: isFavorite });

  const CardHeaderContent = useMemo(() => {
    if (loading) {
      return <CardsHeaderContentSkeleton />;
    }

    return (
      <Box
        display='flex'
        mx='none'
        flexDirection='column'
        width='100%'
        zIndex={99}
        justifyContent='space-between'
        height='100%'
      >
        <Box display='flex' flexDirection='row'>
          <Box
            flex='1'
            display='flex'
            justifyContent='flex-start'
            flexWrap='wrap'
            style={{ gap: gapXS }}
          >
            <ClaimsComponent roles={[Roles.Employee, Roles.Manager]}>
              {inProgress || isCompleted ? (
                <Box
                  px='xxs'
                  py='xxxs'
                  borderRadius='xl'
                  backgroundColor='white'
                  alignItems='center'
                  alignSelf='center'
                  display='flex'
                  flexDirection='row'
                >
                  {inProgress && (
                    <Typography color='primary' variant='p3/light' fontSize='xxs'>
                      {t('inProgress')}
                    </Typography>
                  )}
                  {isCompleted && (
                    <>
                      <Svg fill='primary' name='check' size='xs' />
                      <Typography color='primary' variant='p3/light' fontSize='xxs'>
                        {t('completed')}
                      </Typography>
                    </>
                  )}
                </Box>
              ) : (
                // eslint-disable-next-line react/jsx-no-useless-fragment
                <></>
              )}
            </ClaimsComponent>
            {isSkillToBoost && (!halfHeightLayout || (!inProgress && !isCompleted)) ? (
              <Box
                px='xxs'
                py='xxxs'
                borderRadius='xl'
                alignItems='center'
                alignSelf='center'
                display='flex'
                flexDirection='row'
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}
              >
                <Svg fill='white' name='rocket' size='xs' />
                <Typography color='white' variant='p3/light' ml='xxxs' fontSize='xxs'>
                  {t('skillToBoost')}
                </Typography>
              </Box>
            ) : null}
          </Box>
          <Box
            alignItems='center'
            style={{ gap: gapXS }}
            flex='0'
            display='flex'
            justifyContent='flex-end'
          >
            {rating ? (
              <Box display='flex' flexDirection='row' alignItems='center'>
                <Svg name='star' stroke='primary/text' size='s' />
                <Typography variant='p2' ml='xxs' color='primary/text'>
                  {rating.toFixed(1)}
                </Typography>
              </Box>
            ) : null}

            {/* CREDITS */}
            {creditsCost ? (
              <Box display='flex' flexDirection='row' alignItems='center'>
                <Svg name='wallet' stroke='primary/text' size='xs' />
                <Typography variant='p2' color='primary/text' ml='xxs'>
                  {t('credits', { credits: creditsCost })}
                </Typography>
              </Box>
            ) : null}

            {/* FAVORITE */}
            {!hideFavoriteButton ? (
              <ClaimsComponent roles={[Roles.Employee, Roles.Manager]}>
                <Box display='flex' flexDirection='row' alignItems='center'>
                  <Button
                    onClick={onToggleFavorite}
                    variant='positive'
                    spinnerColor='primary/text'
                    padding='none'
                    minWidth='auto'
                    size='m'
                    loading={isLocalLoading}
                    disabled={isLocalLoading}
                  >
                    <Svg
                      name={localIsFavorite ? 'bookmark' : 'emptyBookmark'}
                      size='xs'
                      fill='primary'
                    />
                  </Button>
                </Box>
              </ClaimsComponent>
            ) : null}
          </Box>
        </Box>
        {title || category ? (
          <Box
            display='flex'
            flexWrap='wrap'
            justifyContent='flex-start'
            flexDirection='column'
            flex={1}
            mt='s'
          >
            {category ? (
              <Typography variant='p3/light' color='primary/text'>
                {category}
              </Typography>
            ) : null}
            {title ? <Typography variant='h4'>{title}</Typography> : null}
          </Box>
        ) : null}
        {/* MAIN INFO CONTENT */}
        {mainInfoContent ? (
          <Box display='flex' flexWrap='wrap' justifyContent='flex-start' style={{ gap: gapS }}>
            <Box display='flex' flexDirection='row' alignItems='center'>
              <Svg name={mainInfoContent.iconName} fill='primary/text' size='xs' />
              <Typography variant='p2' ml='xxs' color='primary/text'>
                {mainInfoContent.text}
              </Typography>
            </Box>
          </Box>
        ) : null}
      </Box>
    );
  }, [
    loading,
    gapXS,
    inProgress,
    isCompleted,
    t,
    isSkillToBoost,
    halfHeightLayout,
    rating,
    creditsCost,
    onToggleFavorite,
    isLocalLoading,
    localIsFavorite,
    title,
    category,
    mainInfoContent,
    gapS,
    hideFavoriteButton,
  ]);

  return (
    <Box
      p='xs'
      display='flex'
      borderRadius='m'
      borderBottomLeftRadius={0}
      borderBottomRightRadius={0}
      alignItems='center'
      flexDirection='row'
      height={cardHeaderHeight}
      background={
        loading
          ? undefined
          : `linear-gradient(
              to bottom,
              ${primaryColor},
              transparent
            ),
            conic-gradient(from 0.6725turn at 90% 35%, ${primaryColor} 1rad, ${primaryBackgroundColor} 4rad)`
      }
      position='relative'
      flex={halfHeightLayout ? 1 : undefined}
      style={{ opacity: isCompleted && isEmployeeOrManager ? 0.5 : 1 }}
    >
      {!halfHeightLayout ? CardHeaderContent : null}
      {halfHeightLayout &&
        ((isSkillToBoost && !inProgress && !isCompleted) || inProgress || isCompleted) && (
          <ClaimsComponent roles={[Roles.Employee, Roles.Manager]}>
            <Box
              position='absolute'
              top='xs'
              left='xs'
              zIndex={99}
              display='flex'
              style={{ gap: gapXS }}
            >
              {isSkillToBoost && !inProgress && !isCompleted && (
                <Box
                  px='xxs'
                  py='xxxs'
                  borderRadius='xl'
                  alignItems='center'
                  display='flex'
                  flexDirection='row'
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}
                >
                  <Svg fill='white' name='rocket' size='xs' />
                  <Typography color='white' variant='p3/light' ml='xxxs' fontSize='xxs'>
                    {t('skillToBoost')}
                  </Typography>
                </Box>
              )}
              {(inProgress || isCompleted) && (
                <Box
                  px='xxs'
                  py='xxxs'
                  borderRadius='xl'
                  backgroundColor='white'
                  alignItems='center'
                  display='flex'
                  flexDirection='row'
                >
                  {inProgress && (
                    <Typography color='primary' variant='p3/light' fontSize='xxs'>
                      {t('inProgress')}
                    </Typography>
                  )}
                  {isCompleted && (
                    <>
                      <Svg fill='primary' name='check' size='xs' />
                      <Typography color='primary' variant='p3/light' fontSize='xxs'>
                        {t('completed')}
                      </Typography>
                    </>
                  )}
                </Box>
              )}
            </Box>
          </ClaimsComponent>
        )}
      {imageAssetName || thumbnailUrl ? (
        <Box
          style={{ mixBlendMode: 'luminosity' }}
          left='0'
          right='0'
          bottom='0'
          top='0'
          display='flex'
          position={!halfHeightLayout ? 'absolute' : undefined}
        >
          {!loading ? (
            <Asset
              width={assetSize}
              domain='images'
              name={imageAssetName}
              src={thumbnailUrl}
              marginLeft='auto'
              marginRight='auto'
              maxWidth='100%'
              maxHeight='100%'
            />
          ) : null}
        </Box>
      ) : null}
      <Box
        position='absolute'
        left={0}
        bottom={0}
        width='100%'
        height='100%'
        backgroundColor={loading ? 'transparent' : 'primary'}
        opacity={0.3}
      />
    </Box>
  );
};
